import {
  analyzeCategory,
  CATEGORY_LABELS,
  hasCategoryEvidence,
  debugCategoryDecision,
  splitCategoryClauses,
} from './categoryClassifier';
import { buildConciseBehaviorTitle } from './behaviorTitles';
import { buildConciseMilestoneTitle } from './milestoneTitles';
import {
  CARE_BEHAVIOR_REFUSAL_PATTERN,
  CARE_MEDICATION_DOSE_MG_PATTERN,
  CARE_MEDICATION_NAME_PATTERN,
} from '../shared/careSignalPatterns';
import { buildQuickNoteFoodTitle } from '../shared/careFoodTitles';
import { isQuickNoteContinuationText } from '../shared/careContinuationRules';
import { buildNaturalCaptureCategoryDecision } from '../components/Dashboard/ImportLogs/naturalCaptureCategoryCore';
import { extractCareTimeFromText } from '../shared/careDateTimePatterns';

export { QUICK_TAG_CATEGORY_MAP } from '../constants/logTypeRegistry';

const normalizeEntityText = (text = '') => String(text || '')
  .replace(/\s+/g, ' ')
  .replace(/^(?:mood|sleep|food|medication|behavior|activity|milestone)\s*[:-]\s*/i, '')
  .trim();

const extractQuickNoteTime = (text = '') => {
  const extracted = extractCareTimeFromText(text);
  return extracted.time || null;
};

const hasQuickBehaviorActionSignal = (text = '') => (
  CARE_BEHAVIOR_REFUSAL_PATTERN.test(String(text || ''))
  || /\b(?:cry|cried|crying|aggress(?:ion|ive)?|hit|kick(?:ed|ing)?|bit(?:e|ing)?|throw(?:ing|n)?|redirect(?:ed|ion)?|needed redirection|needed reminders?|needed prompting|needed support|one on one support|one-on-one support|transition difficulty|difficulty with transition|struggled with transition|sat out|needed a break|took a break|rejoin(?:ed|ing)?|returned to (?:group|class|activity)|distracted|off task|off-task|trouble focusing|difficulty focusing)\b/i.test(String(text || ''))
);

const hasQuickMoodOnlySignal = (text = '') => (
  /\b(?:calm|happy|cheerful|quiet|tired|sad|content|worried|anxious|irritable|cranky|upset|frustrated)\b/i.test(String(text || ''))
  && !hasQuickBehaviorActionSignal(text)
);

const hasQuickMedicationCue = (text = '') => hasCategoryEvidence('medication', text);
const hasQuickFoodCue = (text = '') => hasCategoryEvidence('food', text);
const hasQuickActivityCue = (text = '') => hasCategoryEvidence('activity', text);
const hasQuickMoodCue = (text = '') => hasCategoryEvidence('mood', text);
const hasQuickDoctorVisitCue = (text = '') => (
  /\bDr\.\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/i.test(String(text || ''))
  && /\b(?:saw|visit(?:ed|ing)?|appointment|follow[- ]?up|today|concern|concerns)\b/i.test(String(text || ''))
);

const classifyQuickNoteEntityCategory = (text = '', previousCategory = null) => {
  const analysis = analyzeCategory(text, { previousCategory, allowUnknown: true });
  if (analysis.category && analysis.category !== 'unknown') {
    if (analysis.category === 'mood') {
      return 'log';
    }

    return analysis.category;
  }

  if (hasQuickBehaviorActionSignal(text)) {
    return 'behavior';
  }

  if (hasQuickMoodOnlySignal(text)) {
    return 'log';
  }

  if (!analysis.category || analysis.category === 'unknown') {
    return null;
  }

  return analysis.category === 'mood' ? 'log' : analysis.category;
};

const getQuickNoteOriginalCategory = (text = '', previousCategory = null) => (
  previousCategory
    ? classifyQuickNoteEntityCategory(text, previousCategory)
    : classifyQuickNoteCategory(text)
);

const getQuickNoteSharedCategoryDecision = (text = '', previousCategory = null, originalCategory = null) => (
  buildNaturalCaptureCategoryDecision(text, {
    currentPathCategory: originalCategory || getQuickNoteOriginalCategory(text, previousCategory),
    previousCategory,
    sourceMode: 'quick_log',
    inputMode: 'quick_log',
    parserMode: 'quick_log',
    shapeHint: previousCategory ? 'quick_log_entity' : 'quick_log',
    categorySource: 'quick_log',
  })
);

const resolveQuickNoteCategoryPromotion = ({
  text = '',
  originalCategory = null,
  sharedDecision = null,
} = {}) => {
  const normalizedText = String(text || '').trim();
  const normalizedOriginal = String(originalCategory || '').trim().toLowerCase() || null;
  const sharedProposed = String(sharedDecision?.proposedCategory || '').trim().toLowerCase() || null;
  const sharedFinal = String(sharedDecision?.finalCategory || '').trim().toLowerCase() || null;

  const explicitBehavior = hasQuickBehaviorActionSignal(normalizedText);
  const explicitMood = hasQuickMoodCue(normalizedText);
  const explicitFood = hasQuickFoodCue(normalizedText);
  const explicitMedication = hasQuickMedicationCue(normalizedText);
  const explicitActivity = hasQuickActivityCue(normalizedText);

  if (!normalizedOriginal) {
    const nextCategory = sharedFinal || sharedProposed || null;
    return {
      finalCategory: nextCategory,
      promotionApplied: Boolean(nextCategory),
      promotionReason: nextCategory ? 'no_current_category' : 'no_proposed_category',
    };
  }

  if (normalizedOriginal === 'log' || normalizedOriginal === 'unknown') {
    const nextCategory = sharedFinal || sharedProposed || null;
    return {
      finalCategory: nextCategory || normalizedOriginal,
      promotionApplied: Boolean(nextCategory && nextCategory !== normalizedOriginal),
      promotionReason: nextCategory && nextCategory !== normalizedOriginal ? 'shared_category_promoted' : 'already_matching',
    };
  }

  if ((normalizedOriginal === 'food' || normalizedOriginal === 'log') && explicitBehavior) {
    return {
      finalCategory: 'behavior',
      promotionApplied: true,
      promotionReason: 'behavior_over_food',
    };
  }

  if ((normalizedOriginal === 'food' || normalizedOriginal === 'log') && explicitMedication) {
    return {
      finalCategory: 'medication',
      promotionApplied: true,
      promotionReason: 'medication_over_food',
    };
  }

  if ((normalizedOriginal === 'food' || normalizedOriginal === 'log') && explicitMood && !explicitBehavior) {
    return {
      finalCategory: 'mood',
      promotionApplied: true,
      promotionReason: 'explicit_mood_over_food',
    };
  }

  if ((normalizedOriginal === 'mood' || normalizedOriginal === 'log') && explicitFood && !explicitMood && !explicitBehavior && !explicitMedication) {
    return {
      finalCategory: 'food',
      promotionApplied: true,
      promotionReason: 'food_over_mood',
    };
  }

  if (normalizedOriginal === 'sleep' && explicitMedication) {
    return {
      finalCategory: 'medication',
      promotionApplied: true,
      promotionReason: 'medication_over_sleep',
    };
  }

  if (normalizedOriginal === 'activity' && explicitActivity && sharedFinal === 'sleep') {
    return {
      finalCategory: 'activity',
      promotionApplied: false,
      promotionReason: 'preserve_activity_over_sleep',
    };
  }

  if (normalizedOriginal === 'medication' && explicitMedication && sharedFinal === 'sleep') {
    return {
      finalCategory: 'medication',
      promotionApplied: false,
      promotionReason: 'preserve_medication_over_sleep',
    };
  }

  if (sharedFinal && sharedFinal !== normalizedOriginal && sharedDecision?.promotionApplied) {
    return {
      finalCategory: sharedFinal,
      promotionApplied: true,
      promotionReason: sharedDecision?.promotionReason || 'shared_category_promoted',
    };
  }

  return {
    finalCategory: normalizedOriginal,
    promotionApplied: false,
    promotionReason: 'already_matching',
  };
};

export const buildQuickNoteCategoryDecision = (text = '', {
  previousCategory = null,
} = {}) => {
  const rawText = String(text || '').trim();
  const originalCategory = getQuickNoteOriginalCategory(rawText, previousCategory);
  const sharedDecision = getQuickNoteSharedCategoryDecision(rawText, previousCategory, originalCategory);
  const explicitBehavior = hasQuickBehaviorActionSignal(rawText);
  const explicitMood = hasQuickMoodCue(rawText);
  const explicitFood = hasQuickFoodCue(rawText);
  const explicitMedication = hasQuickMedicationCue(rawText);
  const explicitActivity = hasQuickActivityCue(rawText);
  const promotion = resolveQuickNoteCategoryPromotion({
    text: rawText,
    originalCategory,
    sharedDecision,
  });

  const proposedCategory = String(sharedDecision?.proposedCategory || '').trim().toLowerCase() || null;
  const sharedFinalCategory = String(sharedDecision?.finalCategory || '').trim().toLowerCase() || null;

  return {
    originalCategory,
    proposedCategory,
    sharedFinalCategory,
    finalCategory: promotion.finalCategory || originalCategory || proposedCategory || sharedFinalCategory || null,
    promotionApplied: Boolean(promotion.promotionApplied),
    promotionReason: promotion.promotionReason || 'already_matching',
    sharedDecision,
    debug: {
      originalCategory,
      proposedCategory,
      sharedFinalCategory,
      category: promotion.finalCategory || originalCategory || proposedCategory || sharedFinalCategory || 'unknown',
      confidence: sharedDecision?.confidence || 'none',
      promotionApplied: Boolean(promotion.promotionApplied),
      promotionReason: promotion.promotionReason || 'already_matching',
      signals: [
        ...(explicitBehavior ? ['behavior'] : []),
        ...(explicitMedication ? ['medication'] : []),
        ...(explicitFood ? ['food'] : []),
        ...(explicitActivity ? ['activity'] : []),
        ...(explicitMood ? ['mood'] : []),
      ],
    },
  };
};

const buildBehaviorQuickNoteTitle = (text = '') => buildConciseBehaviorTitle(text);

const buildQuickNoteEntityTitle = (category, text) => {
  const normalized = normalizeEntityText(text);
  if (!normalized) {
    return CATEGORY_LABELS[category] || category;
  }

  const firstSentence = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;

  if (category === 'behavior') {
    return buildBehaviorQuickNoteTitle(firstSentence);
  }

  if (category === 'food') {
    return buildQuickNoteFoodTitle(normalized);
  }

  if (category === 'medication') {
    const doseMatch = normalized.match(CARE_MEDICATION_DOSE_MG_PATTERN);
    const medNameMatch = normalized.match(CARE_MEDICATION_NAME_PATTERN);
    if (medNameMatch?.[0] && doseMatch?.[0]) {
      const medicationName = medNameMatch[0]
        .split(/\s+/)
        .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
        .join(' ');
      return `${medicationName} ${doseMatch[0]}`.replace(/\s+/g, ' ').trim();
    }
    if (medNameMatch?.[0]) {
      const medicationName = medNameMatch[0]
        .split(/\s+/)
        .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
        .join(' ');
      return `${medicationName} given`;
    }
    if (/\b(?:gave|given|administered|took)\b/i.test(firstSentence)) {
      return 'Medication given';
    }
  }

  if (category === 'doctor_visit') {
    return 'Doctor visit';
  }

  if (category === 'sleep') {
    return 'Sleep';
  }

  if (category === 'milestone') {
    return buildConciseMilestoneTitle(normalized);
  }

  if (category === 'log') {
    const moodMatch = firstSentence.match(/\b(calm|calmer|happy|sad|anxious|quiet|tired|content|excited|worried|upset|frustrat(?:ed|ing)|irritable|cranky|angry)\b/i);
    if (moodMatch?.[1]) {
      return `${moodMatch[1].charAt(0).toUpperCase()}${moodMatch[1].slice(1).toLowerCase()} note`;
    }
    return 'Note';
  }

  if (category === 'mood') {
    const moodMatch = firstSentence.match(/\b(calm|calmer|happy|sad|anxious|quiet|tired|content|excited|worried|upset|frustrat(?:ed|ing)|irritable|cranky|angry)\b/i);
    if (moodMatch?.[1]) {
      return `${moodMatch[1].charAt(0).toUpperCase()}${moodMatch[1].slice(1).toLowerCase()} note`;
    }
    return 'Mood note';
  }

  if (category === 'activity') {
    const activityMatch = firstSentence.match(/\b(participated|joined|worked|playing|played|returned to class|transitioned|arrived|recess|playground|classwork|group activity|outside time|outdoor play)\b/i);
    return activityMatch?.[1] ? activityMatch[1].charAt(0).toUpperCase() + activityMatch[1].slice(1).toLowerCase() : 'Activity';
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const normalizeQuickNoteReviewRow = (row = {}, {
  parserMode = 'quick_log',
  sourceMode = 'quick_log',
  inputMode = 'quick_log',
  rawText = '',
  captureMode = '',
  routingReason = '',
  localConfidence = '',
  usedBackendExtraction = false,
  structureDecision = null,
} = {}) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const canonicalText = String(
    row?.body
    || row?.note
    || row?.text
    || row?.content
    || row?.details
    || row?.description
    || row?.summary
    || row?.value
    || row?.sourceText
    || row?.originalText
    || rawText
    || ''
  ).trim();
  const canonicalTitle = String(row?.title || row?.titlePrefix || '').trim();
  const metadata = {
    ...(row?.metadata || {}),
    canonicalRowVersion: row?.canonicalRowVersion || 1,
    parserMode: row?.parserMode || parserMode,
    sourceMode: row?.sourceMode || sourceMode,
    inputMode: row?.inputMode || inputMode,
    rawText: row?.rawText || rawText || canonicalText || '',
    rawSourceText: row?.rawSourceText || rawText || canonicalText || '',
  };

  if (captureMode) {
    metadata.captureMode = captureMode;
  }

  if (routingReason) {
    metadata.routingReason = routingReason;
  }

  if (localConfidence) {
    metadata.localConfidence = localConfidence;
  }

  if (usedBackendExtraction) {
    metadata.usedBackendExtraction = true;
  }

  if (structureDecision) {
    metadata.structureDecision = structureDecision;
  }

  return {
    ...row,
    title: canonicalTitle || row?.title || row?.titlePrefix || '',
    titlePrefix: String(row?.titlePrefix || row?.label || row?.category || '').trim(),
    body: canonicalText,
    note: canonicalText,
    text: canonicalText,
    content: canonicalText,
    details: canonicalText,
    description: canonicalText,
    summary: canonicalText,
    value: canonicalText,
    sourceText: String(row?.sourceText || row?.originalText || rawText || canonicalText || '').trim(),
    originalText: String(row?.originalText || row?.sourceText || rawText || canonicalText || '').trim(),
    parserMode: row?.parserMode || parserMode,
    sourceMode: row?.sourceMode || sourceMode,
    inputMode: row?.inputMode || inputMode,
    rawText: row?.rawText || rawText || canonicalText || '',
    rawSourceText: row?.rawSourceText || rawText || canonicalText || '',
    source: row?.source || 'quick_log',
    mode: row?.mode || inputMode,
    canonicalRowVersion: row?.canonicalRowVersion || 1,
    metadata,
  };
};

export const normalizeQuickNoteReviewRows = (rows = [], options = {}) => (
  Array.isArray(rows)
    ? rows.map((row) => normalizeQuickNoteReviewRow(row, options))
    : []
);

export const classifyQuickNoteCategory = (inputText = '') => {
  const analysis = analyzeCategory(inputText, { allowUnknown: true });
  return analysis.category && analysis.category !== 'unknown' ? analysis.category : 'log';
};

export const extractQuickNoteEntities = (inputText = '') => {
  const rawText = String(inputText || '').trim();
  if (!rawText) {
    return [];
  }

  const clauses = splitCategoryClauses(rawText, { enableFoodBehaviorBoundarySplit: true });
  const entities = [];
  const doctorVisitDetected = hasQuickDoctorVisitCue(rawText);

  clauses.forEach((clause) => {
    const previousCategory = entities.length > 0 ? entities[entities.length - 1].category : null;
    const categoryDecision = buildQuickNoteCategoryDecision(clause, { previousCategory });
    const category = categoryDecision.finalCategory;

    if (!category || category === 'unknown') {
      debugCategoryDecision('quick-note-clause-skip', clause, categoryDecision.debug || analyzeCategory(clause, { allowUnknown: true }));
      return;
    }

    const entityText = normalizeEntityText(clause);
    if (!entityText) {
      debugCategoryDecision('quick-note-empty-entity', clause, analyzeCategory(clause, { allowUnknown: true }));
      return;
    }

    const clauseTime = extractQuickNoteTime(entityText);
    const previousEntity = entities.length > 0 ? entities[entities.length - 1] : null;
    const shouldMergeWithPrevious = Boolean(
      previousEntity
      && previousEntity.category === category
      && (
        (clauseTime && previousEntity.time && clauseTime === previousEntity.time)
        || (!clauseTime && isQuickNoteContinuationText(category, entityText))
      )
    );

    if (shouldMergeWithPrevious) {
      previousEntity.texts.push(entityText);
      previousEntity.text = previousEntity.texts.join('; ');
      previousEntity.title = buildQuickNoteEntityTitle(category, previousEntity.text);
      return;
    }

    const entity = {
      category,
      label: category === 'log' ? 'Note' : (CATEGORY_LABELS[category] || category),
      title: buildQuickNoteEntityTitle(category, entityText),
      titlePrefix: category === 'log' ? 'Note' : (CATEGORY_LABELS[category] || category),
      texts: [entityText],
      text: entityText,
      time: clauseTime,
      needsReview: category === 'medication' && /\b(?:started|start(?:ed|ing)?|new|newly)\b/i.test(entityText),
      reviewReasons: category === 'medication' && /\b(?:started|start(?:ed|ing)?|new|newly)\b/i.test(entityText)
        ? ['new_medication_needs_review']
        : [],
    };
    entities.push(entity);

    debugCategoryDecision('quick-note-entity', clause, categoryDecision.debug || analyzeCategory(clause, { allowUnknown: true }));
  });

  if (doctorVisitDetected) {
    entities.unshift({
      category: 'doctor_visit',
      label: 'Doctor visit',
      title: buildQuickNoteEntityTitle('doctor_visit', rawText),
      titlePrefix: 'Doctor visit',
      text: rawText,
      time: null,
      needsReview: true,
      reviewReasons: ['provider_unknown', 'follow_up_needed'],
    });
  }

  return entities.map((entity) => ({
    category: entity.category,
    label: entity.label,
    title: entity.title,
    titlePrefix: entity.titlePrefix,
    text: entity.text,
    time: entity.time || null,
    needsReview: Boolean(entity.needsReview),
    reviewReasons: Array.isArray(entity.reviewReasons) ? entity.reviewReasons : [],
  }));
};

export const summarizeQuickNoteEntities = (inputText = '') => (
  extractQuickNoteEntities(inputText).map((entity) => entity.category)
);
